import ListItem, { ListItemProps } from '@ds/components/data-display/ListItem';
import Divider, { DividerProps } from '@ds/components/layout/Divider';
import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingList,
    FloatingPortal,
    offset,
    Placement,
    shift,
    size,
    type SizeOptions,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import React from 'react';

import styles from './Menu.module.css';
import MenuButton from './MenuButton';

interface MenuContextValue {
    activeIndex: number | null;
    getItemProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>;
    close: () => void;
    /** A `Menu.Header` registers its id while it is mounted; the menu points `aria-describedby` at it. */
    registerDescription: (id: string) => () => void;
}

const MenuContext = React.createContext<MenuContextValue | null>(null);

export interface MenuProps {
    /** The element that opens the menu. Cloned to attach the anchor ref + aria-haspopup/expanded + handlers. */
    trigger: React.ReactElement;
    /** Menu content — `Menu.Item`, `Menu.Header`, `Menu.Divider`. */
    children: React.ReactNode;
    /** Controlled open state. Omit for uncontrolled (internal) state. */
    open?: boolean;
    /** Called when the open state should change (both controlled and uncontrolled). */
    onOpenChange?: (open: boolean) => void;
    /** Preferred placement relative to the trigger. @default 'bottom-start' */
    placement?: Placement;
    /** Fixed width of the surface. Omit to size to content. */
    width?: number | string;
    /** Minimum width of the surface. @default 200 */
    minWidth?: number | string;
    /** Class for the floating surface. */
    className?: string;
}

/**
 * `Menu` — the modern action / navigation overlay (the Figma "Dropdown" Nav and
 * Table-Action variants). A floating list whose rows fire an action and dismiss;
 * it keeps **no** selected value (that is the deferred `Select`). Built on
 * Floating UI for React (see ADR 0011): it owns positioning (8px offset, flip,
 * shift, max-height→scroll), outside-click / Esc dismissal, focus return to the
 * trigger, and roving keyboard focus with `role="menu"` / `role="menuitem"`.
 *
 * @example
 * <Menu trigger={<Button endIcon={<ChevronDown />}>Actions</Button>}>
 *     <Menu.Item onSelect={editProfile}>Edit eNVD profile</Menu.Item>
 *     <Menu.Divider />
 *     <Menu.Item danger onSelect={logout} endAdornment={<LogoutIcon />}>
 *         Logout from eNVD
 *     </Menu.Item>
 * </Menu>
 */
const Menu: React.FunctionComponent<MenuProps> & {
    Item: typeof MenuItem;
    Header: typeof MenuHeader;
    Divider: typeof MenuDivider;
    Button: typeof MenuButton;
} = ({
    trigger,
    children,
    open: controlledOpen,
    onOpenChange,
    placement = 'bottom-start',
    width,
    minWidth = 200,
    className,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
    const open = controlledOpen ?? uncontrolledOpen;

    const setOpen = React.useCallback(
        (next: boolean) => {
            onOpenChange?.(next);
            if (controlledOpen === undefined) {
                setUncontrolledOpen(next);
            }
        },
        [controlledOpen, onOpenChange]
    );

    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const elementsRef = React.useRef<Array<HTMLElement | null>>([]);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip({ padding: 8 }),
            shift({ padding: 8 }),
            size({
                padding: 8,
                apply({ availableHeight, elements }) {
                    elements.floating.style.maxHeight = `${Math.min(availableHeight, 400)}px`;
                },
            } satisfies SizeOptions),
        ],
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const listNavigation = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        loop: true,
        // Floating UI skips `aria-disabled` rows by default. The ARIA menu pattern keeps a disabled item focusable so
        // it is announced as unavailable, so no index is disabled for navigation; `Menu.Item` withholds activation.
        disabledIndices: [],
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        click,
        dismiss,
        role,
        listNavigation,
    ]);

    const close = React.useCallback(() => setOpen(false), [setOpen]);

    // Ids of the mounted `Menu.Header`s. The menu references one only while it is rendered, so it never points
    // `aria-describedby` at a missing element.
    const [descriptionIds, setDescriptionIds] = React.useState<string[]>([]);
    const registerDescription = React.useCallback((id: string) => {
        setDescriptionIds((ids) => [...ids, id]);
        return () => setDescriptionIds((ids) => ids.filter((existing) => existing !== id));
    }, []);

    const contextValue = React.useMemo<MenuContextValue>(
        () => ({ activeIndex, getItemProps, close, registerDescription }),
        [activeIndex, getItemProps, close, registerDescription]
    );

    // Clone the caller's trigger to attach the floating reference + interaction props,
    // merging any ref/handlers the trigger already had.
    const triggerRef = useMergeRefs([refs.setReference, (trigger as { ref?: React.Ref<unknown> }).ref]);
    const triggerNode = React.cloneElement(trigger, {
        ref: triggerRef,
        ...getReferenceProps({ ...trigger.props }),
    });

    return (
        <>
            {triggerNode}
            <MenuContext.Provider value={contextValue}>
                {open && (
                    <FloatingPortal>
                        <FloatingFocusManager
                            context={context}
                            modal={false}
                        >
                            <div
                                ref={refs.setFloating}
                                className={clsx(styles.menu, className)}
                                style={{ ...floatingStyles, width, minWidth }}
                                {...getFloatingProps()}
                                aria-describedby={descriptionIds.length > 0 ? descriptionIds.join(' ') : undefined}
                            >
                                <FloatingList elementsRef={elementsRef}>{children}</FloatingList>
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </MenuContext.Provider>
        </>
    );
};

export interface MenuItemProps extends Omit<ListItemProps, 'as'> {
    /** Fired when the item is activated (click / Enter / Space). */
    onSelect?: () => void;
    /** Disable activation. Stays focusable and announced (aria-disabled), per the ARIA menu pattern. */
    disabled?: boolean;
    /** Close the menu after a successful select. @default true */
    closeOnSelect?: boolean;
}

/** A command row inside a `Menu` — renders a `ListItem` with `role="menuitem"` and roving focus. */
const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
    ({ onSelect, disabled = false, closeOnSelect = true, className, ...rest }, forwardedRef) => {
        const menu = React.useContext(MenuContext);
        const { ref, index } = useListItem();
        const isActive = menu?.activeIndex === index;
        const mergedRef = useMergeRefs([ref, forwardedRef]);

        return (
            <ListItem
                as="button"
                ref={mergedRef}
                role="menuitem"
                tabIndex={isActive ? 0 : -1}
                disabled={disabled}
                aria-disabled={disabled || undefined}
                data-active={isActive || undefined}
                className={clsx(styles.menuItem, className)}
                {...menu?.getItemProps({
                    onClick: () => {
                        if (disabled) {
                            return;
                        }
                        onSelect?.();
                        if (closeOnSelect) {
                            menu.close();
                        }
                    },
                })}
                {...rest}
            />
        );
    }
);
MenuItem.displayName = 'Menu.Item';

export interface MenuHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

/**
 * A non-interactive block of context at the top of a `Menu` (e.g. "Logged in as …"). Its text is the menu's
 * description: the menu's `aria-describedby` points at it, so a screen reader reads it after the menu's name
 * as focus enters the menu. It is not a menu row, so the arrow keys pass over it.
 */
const MenuHeader: React.FunctionComponent<MenuHeaderProps> = ({ children, className, id: idProp, ...rest }) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const registerDescription = React.useContext(MenuContext)?.registerDescription;

    // A layout effect, so the reference is on the menu before it paints and before focus moves into it.
    React.useLayoutEffect(() => registerDescription?.(id), [registerDescription, id]);

    return (
        <div
            id={id}
            role="presentation"
            className={clsx(styles.header, className)}
            {...rest}
        >
            {children}
        </div>
    );
};
MenuHeader.displayName = 'Menu.Header';

/** A separator between groups of items — reuses the design-system `Divider`. */
const MenuDivider: React.FunctionComponent<DividerProps> = ({ className, ...rest }) => (
    <Divider
        className={clsx(styles.divider, className)}
        {...rest}
    />
);
MenuDivider.displayName = 'Menu.Divider';

Menu.Item = MenuItem;
Menu.Header = MenuHeader;
Menu.Divider = MenuDivider;
Menu.Button = MenuButton;
Menu.displayName = 'Menu';

export type { MenuButtonProps } from './MenuButton';
export { Menu };
export default Menu;
